import React from 'react';
import {
    Play, Image as ImageIcon, Sparkles, ChevronDown, ChevronUp,
    TrendingUp, TrendingDown, Eye, MousePointer, DollarSign, Target,
    AlertTriangle, CheckCircle, Pause, Scale, RefreshCw, Award, XCircle
} from 'lucide-react';
import { formatNumber, formatCurrency, formatPercent } from '../utils/helpers';

// Score utilities
const getScoreColor = (score) => {
    if (score >= 7) return 'var(--color-success)';
    if (score >= 5) return 'var(--color-warning)';
    return 'var(--color-error)';
};

const getGrade = (score) => {
    if (score >= 9) return 'A+';
    if (score >= 8) return 'A';
    if (score >= 7) return 'B+';
    if (score >= 6) return 'B';
    if (score >= 5) return 'C';
    return 'D';
};

const getPriorityColor = (priority) => {
    switch (priority) {
        case 'high': return 'var(--color-error)';
        case 'medium': return 'var(--color-warning)';
        case 'low': return 'var(--color-success)';
        default: return 'var(--color-text-secondary)';
    }
};

const getActionIcon = (action) => {
    switch (action) {
        case 'pause': return <Pause size={14} />;
        case 'scale': return <TrendingUp size={14} />;
        case 'test': return <Sparkles size={14} />;
        case 'refresh': return <RefreshCw size={14} />;
        default: return <AlertTriangle size={14} />;
    }
};

const CreativeCard = ({
    creative,
    analysis,
    isExpanded,
    isAnalyzing,
    onToggleExpand,
    onAnalyze,
    isTopPerformer,
    recommendation
}) => {
    const metrics = creative.metrics || {};
    const roas = metrics.roas || 0;
    const hasAnalysis = !!analysis;

    return (
        <div
            className="card"
            style={{
                overflow: 'hidden',
                border: isTopPerformer ? '2px solid var(--color-success)' : undefined,
                position: 'relative'
            }}
        >
            {/* Top Performer Badge */}
            {isTopPerformer && (
                <div style={{
                    position: 'absolute',
                    top: 'var(--space-2)',
                    right: 'var(--space-2)',
                    zIndex: 10,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-1)',
                    padding: 'var(--space-1) var(--space-2)',
                    borderRadius: 'var(--radius-full)',
                    background: 'var(--color-success)',
                    color: 'white',
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 600
                }}>
                    <Award size={12} />
                    Top Performer
                </div>
            )}

            {/* Recommendation Badge */}
            {recommendation && !isTopPerformer && (
                <div style={{
                    position: 'absolute',
                    top: 'var(--space-2)',
                    right: 'var(--space-2)',
                    zIndex: 10,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-1)',
                    padding: 'var(--space-1) var(--space-2)',
                    borderRadius: 'var(--radius-full)',
                    background: recommendation.action === 'pause' ? 'var(--color-error)' :
                        recommendation.action === 'scale' ? 'var(--color-success)' : 'var(--color-warning)',
                    color: 'white',
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 600
                }}>
                    {getActionIcon(recommendation.action)}
                    {recommendation.action.charAt(0).toUpperCase() + recommendation.action.slice(1)}
                </div>
            )}

            {/* Thumbnail */}
            <div style={{
                position: 'relative',
                paddingTop: '56.25%', // 16:9 aspect ratio
                background: 'var(--color-bg-tertiary)',
                overflow: 'hidden'
            }}>
                {creative.isVideo ? (
                    creative.thumbnailUrl ? (
                        <>
                            <img
                                src={creative.thumbnailUrl}
                                alt={creative.name}
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                }}
                            />
                            <div style={{
                                position: 'absolute',
                                bottom: 'var(--space-2)',
                                left: 'var(--space-2)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-1)',
                                padding: 'var(--space-1) var(--space-2)',
                                borderRadius: 'var(--radius-md)',
                                background: 'rgba(0,0,0,0.7)',
                                color: 'white',
                                fontSize: 'var(--font-size-xs)'
                            }}>
                                <Play size={12} /> Video
                            </div>
                        </>
                    ) : (
                        <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'var(--color-text-tertiary)'
                        }}>
                            <Play size={32} />
                            <span style={{ fontSize: 'var(--font-size-xs)', marginTop: 'var(--space-1)' }}>
                                Video
                            </span>
                        </div>
                    )
                ) : creative.imageUrl || creative.thumbnailUrl ? (
                    <img
                        src={creative.imageUrl || creative.thumbnailUrl}
                        alt={creative.name}
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                        }}
                    />
                ) : (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--color-text-tertiary)'
                    }}>
                        <ImageIcon size={32} />
                        <span style={{ fontSize: 'var(--font-size-xs)', marginTop: 'var(--space-1)' }}>
                            No preview
                        </span>
                    </div>
                )}
            </div>

            {/* Content */}
            <div style={{ padding: 'var(--space-4)' }}>
                {/* Name and Status */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-3)' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <h4 style={{
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 600,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}>
                            {creative.name}
                        </h4>
                    </div>
                    <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 'var(--space-1)',
                        padding: '2px var(--space-2)',
                        borderRadius: 'var(--radius-full)',
                        fontSize: 'var(--font-size-xs)',
                        background: creative.status === 'ACTIVE' ? 'var(--color-success-bg)' :
                            creative.status === 'PAUSED' ? 'var(--color-warning-bg)' : 'var(--color-bg-tertiary)',
                        color: creative.status === 'ACTIVE' ? 'var(--color-success)' :
                            creative.status === 'PAUSED' ? 'var(--color-warning)' : 'var(--color-text-secondary)'
                    }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'currentColor' }} />
                        {creative.status}
                    </span>
                </div>

                {/* Key Metrics */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: 'var(--space-2)',
                    marginBottom: 'var(--space-3)'
                }}>
                    <div>
                        <div className="text-secondary" style={{ fontSize: 'var(--font-size-xs)' }}>Spend</div>
                        <div style={{ fontWeight: 600 }}>{formatCurrency(metrics.spend || 0)}</div>
                    </div>
                    <div>
                        <div className="text-secondary" style={{ fontSize: 'var(--font-size-xs)' }}>ROAS</div>
                        <div style={{
                            fontWeight: 600,
                            color: roas >= 2 ? 'var(--color-success)' : roas >= 1 ? 'var(--color-warning)' : 'var(--color-error)'
                        }}>
                            {roas.toFixed(2)}x
                        </div>
                    </div>
                    <div>
                        <div className="text-secondary" style={{ fontSize: 'var(--font-size-xs)' }}>CTR</div>
                        <div style={{ fontWeight: 600 }}>{(metrics.ctr || 0).toFixed(2)}%</div>
                    </div>
                    <div>
                        <div className="text-secondary" style={{ fontSize: 'var(--font-size-xs)' }}>Impressions</div>
                        <div style={{ fontWeight: 600 }}>{formatNumber(metrics.impressions || 0)}</div>
                    </div>
                </div>

                {/* AI Score Badge and Analyze Button */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 'var(--space-2)'
                }}>
                    {hasAnalysis && (
                        <span style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-1)',
                            padding: 'var(--space-1) var(--space-2)',
                            borderRadius: 'var(--radius-md)',
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 600,
                            background: getScoreColor(analysis.overallScore),
                            color: 'white'
                        }}>
                            {getGrade(analysis.overallScore)} ({analysis.overallScore?.toFixed(1)})
                        </span>
                    )}

                    <div style={{ display: 'flex', gap: 'var(--space-2)', marginLeft: 'auto' }}>
                        {hasAnalysis && (
                            <button
                                onClick={onToggleExpand}
                                className="btn btn-ghost"
                                style={{ padding: 'var(--space-2)' }}
                            >
                                {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </button>
                        )}
                        <button
                            onClick={onAnalyze}
                            disabled={isAnalyzing}
                            className={`btn ${hasAnalysis ? 'btn-secondary' : 'btn-primary'}`}
                            style={{ padding: 'var(--space-2) var(--space-3)', fontSize: 'var(--font-size-sm)' }}
                        >
                            {isAnalyzing ? (
                                <>
                                    <RefreshCw size={14} className="spin" /> Analyzing...
                                </>
                            ) : (
                                <>
                                    <Sparkles size={14} /> {hasAnalysis ? 'Re-analyze' : 'Analyze'}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Expanded Analysis */}
            {isExpanded && hasAnalysis && (
                <div style={{
                    padding: 'var(--space-4)',
                    borderTop: '1px solid var(--color-border-light)',
                    background: 'var(--color-bg-secondary)'
                }}>
                    {/* Summary */}
                    {analysis.summary && (
                        <p style={{
                            marginBottom: 'var(--space-4)',
                            fontSize: 'var(--font-size-sm)',
                            color: 'var(--color-text-secondary)'
                        }}>
                            {analysis.summary}
                        </p>
                    )}

                    {/* Score Breakdown */}
                    {analysis.scores && (
                        <div style={{ marginBottom: 'var(--space-4)' }}>
                            <h5 style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, marginBottom: 'var(--space-2)' }}>
                                Score Breakdown
                            </h5>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 'var(--space-2)' }}>
                                {Object.entries(analysis.scores).map(([key, value]) => (
                                    <div
                                        key={key}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 'var(--space-2)',
                                            fontSize: 'var(--font-size-xs)'
                                        }}
                                    >
                                        <div style={{
                                            flex: 1,
                                            height: 4,
                                            background: 'var(--color-bg-tertiary)',
                                            borderRadius: 'var(--radius-full)',
                                            overflow: 'hidden'
                                        }}>
                                            <div style={{
                                                width: `${value * 10}%`,
                                                height: '100%',
                                                background: getScoreColor(value),
                                                borderRadius: 'var(--radius-full)'
                                            }} />
                                        </div>
                                        <span style={{ minWidth: '80px', color: 'var(--color-text-secondary)' }}>
                                            {key.replace(/([A-Z])/g, ' $1').trim()}
                                        </span>
                                        <span style={{ fontWeight: 600, color: getScoreColor(value) }}>
                                            {value?.toFixed(1)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Improvements */}
                    {analysis.improvements && analysis.improvements.length > 0 && (
                        <div style={{ marginBottom: 'var(--space-4)' }}>
                            <h5 style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, marginBottom: 'var(--space-2)' }}>
                                Recommended Improvements
                            </h5>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                                {analysis.improvements.slice(0, 3).map((improvement, idx) => (
                                    <div
                                        key={idx}
                                        style={{
                                            padding: 'var(--space-3)',
                                            borderRadius: 'var(--radius-md)',
                                            background: 'var(--color-surface)',
                                            borderLeft: `3px solid ${getPriorityColor(improvement.priority)}`
                                        }}
                                    >
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 'var(--space-2)',
                                            marginBottom: 'var(--space-1)'
                                        }}>
                                            <span style={{
                                                padding: '2px 6px',
                                                borderRadius: 'var(--radius-sm)',
                                                fontSize: 'var(--font-size-xs)',
                                                fontWeight: 600,
                                                textTransform: 'uppercase',
                                                background: getPriorityColor(improvement.priority),
                                                color: 'white'
                                            }}>
                                                {improvement.priority}
                                            </span>
                                            <span style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>
                                                {improvement.title}
                                            </span>
                                        </div>
                                        <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-1)' }}>
                                            {improvement.problem}
                                        </p>
                                        <p style={{ fontSize: 'var(--font-size-xs)' }}>
                                            <strong>Solution:</strong> {improvement.solution}
                                        </p>
                                        {improvement.expectedImpact && (
                                            <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-success)', marginTop: 'var(--space-1)' }}>
                                                Expected: {improvement.expectedImpact}
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* A/B Test Ideas */}
                    {analysis.abTestIdeas && analysis.abTestIdeas.length > 0 && (
                        <div>
                            <h5 style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, marginBottom: 'var(--space-2)' }}>
                                A/B Test Ideas
                            </h5>
                            <ul style={{
                                margin: 0,
                                paddingLeft: 'var(--space-4)',
                                fontSize: 'var(--font-size-xs)',
                                color: 'var(--color-text-secondary)'
                            }}>
                                {analysis.abTestIdeas.map((idea, idx) => (
                                    <li key={idx} style={{ marginBottom: 'var(--space-1)' }}>{idea}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default CreativeCard;
